<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { UserMenu } from '@/features/session';
import { resolveApplicationOrigins } from '@/shared/config';
import { formatRelativeDate } from '@/shared/lib';
import { externalLinkMenuItems, IconLogo } from '@/shared/ui';

import { PLATFORM_NAVIGATION, type PlatformNavigationItem } from '../config/platform-navigation';

defineEmits<{
  search: [];
}>();

const route = useRoute();

const isActive = (item: PlatformNavigationItem): boolean =>
  typeof route.name === 'string' && (route.name === item.name || route.name.startsWith(`${item.name}.`));

const userMenuItems = externalLinkMenuItems('app', 'Приложение', () => resolveApplicationOrigins().app.toString());

/** В dev-сборке sha равен `dev`, строка не нужна. */
const buildLabel = computed(() =>
  __APP_GIT_SHA__ === 'dev'
    ? null
    : `сборка ${__APP_GIT_SHA__.slice(0, 7)} · ${formatRelativeDate(__APP_BUILD_TIME__)}`,
);
</script>

<template>
  <div class="flex h-full flex-col gap-6 px-3 py-4">
    <RouterLink
      :to="{ name: 'platform.overview' }"
      class="flex min-h-11 items-center gap-3 rounded-xl px-2 focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none"
    >
      <IconLogo
        :width="32"
        :height="32"
        class="text-(--base-accent-color)"
      />
      <span class="flex flex-col leading-none">
        <span class="text-base font-semibold tracking-tight">Морс</span>
        <span class="mt-1 text-xs text-(--base-secondary-text-color)">Штаб</span>
      </span>
    </RouterLink>

    <button
      type="button"
      class="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-white/8 px-3 text-sm text-(--base-secondary-text-color) transition-colors hover:bg-white/5 hover:text-(--base-text-color) focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none"
      @click="$emit('search')"
    >
      Найти
      <kbd class="font-sans text-xs">⌘K</kbd>
    </button>

    <nav
      aria-label="Разделы штаба"
      class="flex flex-col gap-5"
    >
      <div
        v-for="group in PLATFORM_NAVIGATION"
        :key="group.id"
        class="flex flex-col gap-1"
      >
        <p
          v-if="group.label"
          class="px-3 pb-1 text-xs font-medium text-(--base-secondary-text-color)"
        >
          {{ group.label }}
        </p>
        <RouterLink
          v-for="item in group.items"
          :key="item.name"
          :to="{ name: item.name }"
          :aria-current="isActive(item) ? 'page' : undefined"
          class="flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none"
          :class="
            isActive(item)
              ? 'bg-white/8 text-(--base-text-color)'
              : 'text-(--base-secondary-text-color) hover:bg-white/5 hover:text-(--base-text-color)'
          "
        >
          <component
            :is="item.icon"
            :width="18"
            :height="18"
            aria-hidden="true"
          />
          {{ item.label }}
        </RouterLink>
      </div>
    </nav>

    <div class="mt-auto flex flex-col gap-2 border-t border-white/8 pt-3">
      <UserMenu :extra-items="userMenuItems" />
      <p
        v-if="buildLabel"
        class="px-2 text-xs text-(--base-secondary-text-color) tabular-nums"
      >
        {{ buildLabel }}
      </p>
    </div>
  </div>
</template>
